import { Injectable } from '@angular/core'
import { Subject } from 'rxjs'
import { debounceTime } from 'rxjs/operators'
import { Tag } from 'src/utils/types'
import { validAwsAccountId, validAwsRegion } from '../../../utils/aws-utils'
import { validAzureSubscriptionId } from '../../../utils/azure-utils'
import { ApplicationType } from '../../services/dtos'
import { RegionsService } from 'src/app/services/regions.service'
import { AllRule, ResourceTypeRule, ResourceGroupRule } from './application.service'

export type AccessMethodType = 'cloudformation' | 'stacksets' | null

interface PersistedState {
  applicationName: string
  applicationType: ApplicationType | ''
  applicationRpo: number
  applicationNotificationEmails: string[]
  sourceAccountId: string
  sourceRegion: string
  targetAccountId: string
  targetRegion: string
  identityProviderId: string
  selectedResources: string[]
  matchingRules: Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>
  currentStep: string
  lastUpdated: number
  // Access method choice for AWS applications (CloudFormation or StackSets)
  accessMethod?: AccessMethodType
  // NOTE: detailed StackSets configuration (topology, account IDs, StackSet names) is
  // owned by StacksetsConfigCacheService,keyed per Arpio account so it survives
  // across app-creation sessions and feeds the update flow too.
}

const STORAGE_KEY_PREFIX = 'arpio_app_creator_state'
const STATE_EXPIRY_HOURS = 24

@Injectable({
  providedIn: 'root',
})
export class ApplicationCreatorState {
  applicationName = ''
  applicationType: ApplicationType | '' = ''
  applicationRpo = 3600
  applicationNotificationEmails: string[] = []
  sourceAccountId = ''
  sourceRegion = ''
  targetAccountId = ''
  targetRegion = ''
  identityProviderId = ''
  selectedResources = new Set<string>()
  matchingRules: Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule> = new Array<Tag>()
  // Phase 2: recovery groups — intake from query param works, but the post-creation "add to group" API call is not yet implemented
  recoveryGroupId: string | null = null
  currentStep = 'details'
  // Access method choice for AWS applications (CloudFormation or StackSets)
  // null means no choice has been made yet for this application creation session
  accessMethod: AccessMethodType = null

  private currentAccountId: string | null = null
  private saveSubject = new Subject<void>()

  constructor() {
    // Debounce saves to avoid excessive localStorage writes
    this.saveSubject.pipe(debounceTime(500)).subscribe(() => {
      this.saveToLocalStorage()
    })
  }

  setAccountId(accountId: string): void {
    if (this.currentAccountId !== accountId) {
      this.currentAccountId = accountId
      this.loadFromLocalStorage()
    }
  }

  private getStorageKey(): string {
    return this.currentAccountId ? `${STORAGE_KEY_PREFIX}_${this.currentAccountId}` : STORAGE_KEY_PREFIX
  }

  // Call this to trigger a debounced save
  markDirty(): void {
    this.saveSubject.next()
  }

  // Save current state to localStorage
  saveToLocalStorage(): void {
    if (!this.currentAccountId) return

    try {
      const state: PersistedState = {
        applicationName: this.applicationName,
        applicationType: this.applicationType,
        applicationRpo: this.applicationRpo,
        applicationNotificationEmails: this.applicationNotificationEmails,
        sourceAccountId: this.sourceAccountId,
        sourceRegion: this.sourceRegion,
        targetAccountId: this.targetAccountId,
        targetRegion: this.targetRegion,
        identityProviderId: this.identityProviderId,
        selectedResources: Array.from(this.selectedResources),
        matchingRules: this.matchingRules,
        currentStep: this.currentStep,
        lastUpdated: Date.now(),
        accessMethod: this.accessMethod,
      }
      localStorage.setItem(this.getStorageKey(), JSON.stringify(state))
    } catch (e) {
      console.error('Error saving application creator state to localStorage:', e)
    }
  }

  // Load saved state from localStorage
  loadFromLocalStorage(): boolean {
    if (!this.currentAccountId) return false

    try {
      const stored = localStorage.getItem(this.getStorageKey())
      if (!stored) return false

      const state: PersistedState = JSON.parse(stored)

      // Check if state is expired
      const hoursOld = (Date.now() - state.lastUpdated) / (1000 * 60 * 60)
      if (hoursOld > STATE_EXPIRY_HOURS) {
        this.clearLocalStorage()
        return false
      }

      // Restore state
      this.applicationName = state.applicationName || ''
      this.applicationType = state.applicationType ?? ''
      this.applicationRpo = state.applicationRpo ?? 3600
      this.applicationNotificationEmails = state.applicationNotificationEmails || []
      this.sourceAccountId = state.sourceAccountId || ''
      this.sourceRegion = state.sourceRegion || ''
      this.targetAccountId = state.targetAccountId || ''
      this.targetRegion = state.targetRegion || ''
      this.identityProviderId = state.identityProviderId || ''
      this.selectedResources = new Set(state.selectedResources || [])
      this.matchingRules = state.matchingRules || []
      this.currentStep = state.currentStep || 'details'
      this.accessMethod = state.accessMethod ?? null

      return true
    } catch (e) {
      console.error('Error loading application creator state from localStorage:', e)
      return false
    }
  }

  // Check if there's saved state without loading it
  hasSavedState(): boolean {
    if (!this.currentAccountId) return false

    try {
      const stored = localStorage.getItem(this.getStorageKey())
      if (!stored) return false

      const state: PersistedState = JSON.parse(stored)
      const hoursOld = (Date.now() - state.lastUpdated) / (1000 * 60 * 60)
      return hoursOld <= STATE_EXPIRY_HOURS
    } catch (e) {
      return false
    }
  }

  // Clear saved state from localStorage
  clearLocalStorage(): void {
    if (!this.currentAccountId) return

    try {
      localStorage.removeItem(this.getStorageKey())
    } catch (e) {
      console.error('Error clearing application creator state from localStorage:', e)
    }
  }

  reset() {
    this.applicationName = ''
    this.applicationType = ''
    this.applicationRpo = 3600
    this.applicationNotificationEmails = []
    this.sourceAccountId = ''
    this.sourceRegion = ''
    this.targetAccountId = ''
    this.targetRegion = ''
    this.identityProviderId = ''
    this.selectedResources = new Set<string>()
    this.matchingRules = []
    this.recoveryGroupId = null
    this.currentStep = 'details'
    this.accessMethod = null
  }

  // Reset and clear localStorage
  resetAndClear(): void {
    this.reset()
    this.clearLocalStorage()
  }

  get sourceEndpointUri() {
    return `${this.applicationType}:${this.sourceAccountId}:${this.sourceRegion}`
  }

  get targetEndpointUri() {
    return `${this.applicationType}:${this.targetAccountId}:${this.targetRegion}`
  }

  validAccountsAndRegions(regionsService: RegionsService): boolean {
    // Must have a cloud provider selected
    if (!this.applicationType) {
      return false
    }

    // First check that we have non-empty region values
    if (!this.sourceRegion || !this.targetRegion) {
      return false
    }

    // Only validate against the region list if regions are loaded
    // This prevents a race condition where validation fails before regions are fetched from API
    if (regionsService.areRegionsLoaded()) {
      if (
        !regionsService.isValidRegion(this.applicationType, this.sourceRegion) ||
        !regionsService.isValidRegion(this.applicationType, this.targetRegion)
      ) {
        return false
      }
    }

    if (this.applicationType == ApplicationType.AWS) {
      return validAwsAccountId(this.sourceAccountId) && validAwsAccountId(this.targetAccountId)
    } else if (this.applicationType == ApplicationType.AZURE) {
      return validAzureSubscriptionId(this.sourceAccountId) && validAzureSubscriptionId(this.targetAccountId)
    } else {
      return true
    }
  }
}
